import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Avatar, Badge, Button } from "gold-ui-kit";
import { LogOut } from "lucide-react";
import { NAV_GROUPS } from "../../data/navigation";
import { isPageDisabled } from "../../data/disabledPages";
import { useAuth } from "../../context/AuthContext";

export const Brand = ({ onClick }) => (
  <button type="button" onClick={onClick} className="flex items-center gap-2.5 text-left">
    <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-300 to-amber-600 p-[2px] shadow-lg shadow-amber-500/20">
      <span className="w-full h-full rounded-[10px] bg-slate-950 flex items-center justify-center">
        <img src="/favicon.png" alt="" className="w-5 h-5 object-contain" />
      </span>
    </span>
    <span className="leading-tight">
      <span className="block font-black tracking-tight text-[15px] text-white">ALTINGROSS</span>
      <span className="block text-[10.5px] font-medium text-amber-300/80">Şube Yönetim Portalı</span>
    </span>
  </button>
);

export const NavList = ({ onNavigate }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <nav className="flex flex-col gap-5">
      {NAV_GROUPS.map((group) => (
        <div key={group.title} className="flex flex-col gap-1">
          <div className="px-3 pb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
            {group.title}
          </div>
          {group.items.map((item) => {
            const active = pathname === item.path;
            const disabled = isPageDisabled(item.path);
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                disabled={disabled}
                title={disabled ? "Bu modül şu anda pasif" : undefined}
                aria-current={active ? "page" : undefined}
                onClick={() => {
                  navigate(item.path);
                  onNavigate?.();
                }}
                data-testid={`menubar-${item.id}`}
                className={`group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-medium transition-colors ${
                  disabled
                    ? "text-slate-600 cursor-not-allowed"
                    : active
                    ? "bg-white/10 text-white"
                    : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`}
              >
                {active && <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full bg-amber-400" />}
                <Icon size={17} className={disabled ? "text-slate-600" : active ? "text-amber-400" : "text-slate-400 group-hover:text-slate-200"} />
                <span className="flex-1 text-left">{item.label}</span>
                {disabled && (
                  <Badge size="xs" radius="full" variant="outline" color="default">
                    Pasif
                  </Badge>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </nav>
  );
};

export const UserCard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const name = user?.fullName || user?.username || "Kullanıcı";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-3">
      <Avatar name={name} size="sm" color="yellow" appearance="solid" showStatus status="online" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[12.5px] font-semibold text-white" title={name}>{name}</div>
        <div className="text-[10.5px] text-slate-400">Çevrimiçi</div>
      </div>
      <Button
        iconOnly
        size="sm"
        variant="text"
        color="danger"
        onClick={handleLogout}
        title="Oturumu Sonlandır"
        aria-label="Oturumu Sonlandır"
        data-testid="menubar-logout-btn"
      >
        <LogOut size={16} />
      </Button>
    </div>
  );
};

const Sidebar = () => {
  const navigate = useNavigate();
  return (
    <aside className="hidden md:flex w-64 shrink-0 flex-col bg-slate-950 border-r border-slate-900">
      <div className="px-5 pt-5 pb-6">
        <Brand onClick={() => navigate("/dashboard")} />
      </div>
      <div className="flex-1 overflow-y-auto px-3">
        <NavList />
      </div>
      <div className="p-3">
        <UserCard />
      </div>
    </aside>
  );
};

export default Sidebar;
